import React, {useEffect, useRef, useState} from 'react';
import useModal from "components/modal/useModal";
import useFetchData from "hooks/useFetchData";
import {Logout} from "assets/icons";
import {useNavigate} from "react-router-dom";
import DropdownMenu from "components/DropdownMenu/DropdownMenu";
import {formatFirstname, formatLastname} from "helper/personName";

/** Initiales affichées comme sur le portail Company (cercle, prénom/nom). */
const LogoutNavDesktop = ({ compact = false }) => {
    const {userData} = useFetchData()
    const {toggle} = useModal();
    const navigate = useNavigate();
    const pRole = localStorage.getItem('rUser')
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    
    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
    
    const displayFirstname = formatFirstname(userData?.firstname ?? userData?.firstName);
    const displayLastname = formatLastname(userData?.lastname ?? userData?.lastName);
    const displayFullName = `${displayFirstname} ${displayLastname}`.trim();

    const getInitials = () => {
        const firstInitial = displayFirstname.charAt(0).toUpperCase();
        const lastInitial = displayLastname.charAt(0).toUpperCase();
        const initials = `${firstInitial}${lastInitial}`.trim();
        return initials || '--';
    };
    
    // Fonction pour mapper le rôle
    const getRoleName = () => {
        const tRole = {
            '200': 'Observateur',
            '205': 'Manageur',
            '210': 'Responsable',
            '215': 'Directeur',
            '220': 'Superviseur',
            '225': 'Administrateur'
        };
        return tRole[pRole] || 'Utilisateur';
    };
    
    const handleLogout = (e) => {
        e.preventDefault();
        toggle('log_d_logout');
    };

    const handleProfil = (e) => {
        e.preventDefault();
        navigate('/personalInformations/myPersonalAccount');
        setIsOpen(false);
    };

    const dropdownItems = [
        {
            label: 'Mon profil',
            onClick: handleProfil
        }
    ];

    dropdownItems.push({
        separator: true
    });

    dropdownItems.push({
        icon: Logout,
        label: 'Se déconnecter',
        onClick: handleLogout,
        iconColor: '#EF4444',
        className: 'text-red-500'
    });
    
    return (
        <>
            <div 
                ref={dropdownRef}
                className={`group relative flex items-center cursor-pointer hover:bg-gray-100 rounded-lg px-2 py-1 transition-colors ${compact ? 'gap-0' : 'gap-3'}`}
                onClick={() => setIsOpen(!isOpen)}
                title={compact && displayFullName ? `${displayFullName} — ${getRoleName()}` : undefined}
                aria-label={compact && displayFullName ? `Menu utilisateur ${displayFullName}` : undefined}
            >
                {/* Initiales (aligné portail Company Workfully) */}
                <div className="min-w-[40px] w-[40px] h-[40px] rounded-full bg-gray-100 border border-gray-100 shadow-sm group-hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <span className="text-sm font-medium text-gray-700">
                        {getInitials()}
                    </span>
                </div>
                {/* Informations utilisateur */}
                {!compact ? (
                <div className="flex flex-col min-w-[120px]">
                    <span className="text-sm font-medium text-gray-700 leading-5 h-5">
                        {displayFullName || '\u00A0'}
                    </span>
                    <span className="text-xs text-gray-500 leading-4 h-4">
                        {getRoleName()}
                    </span>
                </div>
                ) : null}
                
                {/* Dropdown menu */}
                {isOpen && (
                    <div className="absolute top-full right-0 mt-2 w-56 z-50">
                        <DropdownMenu items={dropdownItems} width="w-56" />
                    </div>
                )}
            </div>
        </>
    );
};

export default LogoutNavDesktop;